import React, { useMemo } from 'react';
import { Card, Modal, Typography, Button } from '@scale/scaleui';
import { EmailTemplateData, EmailCommsTemplateStatus } from '@scale/shared-types';
import TemplatePreview from 'react-scale/components/GrowthCenter/EmailDripCampaign/CampaignSetup/EmailTemplateModalV2/TemplatePreview';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface TemplateCardProps {
  template: EmailTemplateData;
}

const TemplateCard = ({ template }: TemplateCardProps) => {
  const templatesLibraryEnabled = useUnleashFeatureFlag('growth-template-library', {}, false);
  const { htmlWrapper, body, name, subject, version, status, sendgridTemplateId } = template;
  const location = useLocation();
  const history = useHistory();

  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const openTemplateId = urlParams.get('openTemplateId');
  const isOpen = openTemplateId === template.templateId;

  const icon =
    status === EmailCommsTemplateStatus.Public
      ? 'earth-americas'
      : status === EmailCommsTemplateStatus.Internal
      ? 'lock'
      : 'trash';

  // Function to open the modal by setting the query param
  const handleOpen = () => {
    const newUrlParams = new URLSearchParams(urlParams);
    newUrlParams.set('openTemplateId', template.templateId);

    history.push({
      pathname: location.pathname,
      search: newUrlParams.toString(),
    });
  };

  // Function to close the modal by removing the query param
  const handleClose = () => {
    const newUrlParams = new URLSearchParams(urlParams);
    newUrlParams.delete('openTemplateId');

    history.push({
      pathname: location.pathname,
      search: newUrlParams.toString(),
    });
  };

  return (
    <>
      <Card
        className="basis-1/4 h-24"
        description={subject}
        descriptionClassName="overflow-ellipsis overflow-hidden"
        onClick={() => handleOpen()}
        title={name}
        titleClassName="whitespace-nowrap overflow-ellipsis overflow-hidden"
        tag={{ label: status, icon }}
      />
      <Modal
        innerClassName="w-2/3 h-full"
        open={isOpen}
        onClose={() => handleClose()}
        title={
          <>
            <Typography.H5 className="font-semibold">{name}</Typography.H5>
            <Typography.Paragraph1 className="font-medium text-sm">
              ID: {template.templateId}
            </Typography.Paragraph1>
            <Typography.Paragraph1 className="font-medium text-sm">
              Version: {version}
            </Typography.Paragraph1>
          </>
        }
        actions={
          templatesLibraryEnabled && (
            <div className="flex flex-row-reverse justify-between items-center">
              <Link to={`${Routes.TemplateSetup}/${template.templateId}`}>
                <Button size="lg" key="edit">
                  Edit
                </Button>
              </Link>
            </div>
          )
        }
      >
        <div className="h-full flex flex-col gap-4">
          <Typography.Paragraph1 className="inline-block">Subject: {subject}</Typography.Paragraph1>
          <div className="relative flex-grow">
            <TemplatePreview
              className="absolute"
              templateHTML={htmlWrapper}
              content={body}
              subject={subject}
              sendgridTemplateId={sendgridTemplateId}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TemplateCard;
